import { useState, useMemo } from 'react';

/**
 * useSort - Custom hook to sort data dynamically
 * @param {Array} data - Array of objects to sort
 * @param {Array|null} initialSort - [field, type, direction] e.g., ['title','alphanumeric','asc']
 */
const useSort = (data, initialSort = null) => {
  const [sortConfig, setSortConfig] = useState(
    initialSort
      ? { field: initialSort[0], type: initialSort[1], direction: initialSort[2] || 'asc' }
      : null
  );

  // Helper to get nested value from object
  const getValue = (obj, path) => path.split('.').reduce((acc, key) => acc?.[key], obj);

  // Alphanumeric natural sort function
  const naturalCompare = (a, b) => {
    const regex = /(\d+)|(\D+)/g;
    const aParts = String(a).match(regex);
    const bParts = String(b).match(regex);

    for (let i = 0; i < Math.max(aParts.length, bParts.length); i += 1) {
      const aPart = aParts[i] || '';
      const bPart = bParts[i] || '';
      const aNum = parseInt(aPart, 10);
      const bNum = parseInt(bPart, 10);

      if (!Number.isNaN(aNum) && !Number.isNaN(bNum)) {
        if (aNum < bNum) return -1;
        if (aNum > bNum) return 1;
      } else {
        const cmp = aPart.localeCompare(bPart);
        if (cmp !== 0) return cmp;
      }
    }

    return 0;
  };

  const sortedData = useMemo(() => {
    if (!sortConfig || !sortConfig.field) return data;

    const { field, type, direction } = sortConfig;

    return [...data].sort((a, b) => {
      let valA = getValue(a, field);
      let valB = getValue(b, field);

      if (valA == null) valA = '';
      if (valB == null) valB = '';

      let result = 0;

      switch (type) {
        case 'number':
          result = Number(valA) - Number(valB);
          break;
        case 'date':
          result = new Date(valA) - new Date(valB);
          break;
        case 'alphanumeric':
          result = naturalCompare(valA, valB);
          break;
        case 'string':
        default: {
          const aStr = String(valA).trim().toLowerCase();
          const bStr = String(valB).trim().toLowerCase();

          // Handle N/A, empty, or invalid strings → always sort last
          const isANullish = !aStr || aStr === 'n/a' || aStr === 'na' || aStr === '-';
          const isBNullish = !bStr || bStr === 'n/a' || bStr === 'na' || bStr === '-';

          if (isANullish && !isBNullish) return 1;
          if (!isANullish && isBNullish) return -1;

          result = aStr.localeCompare(bStr);
          break;
        }
      }

      return direction === 'asc' ? result : -result;
    });
  }, [data, sortConfig]);

  return { sortedData, sortConfig, setSortConfig };
};

export default useSort;
